import React, { useEffect, useState, useMemo } from 'react';
import {
  Datagrid,
  DateField,
  EditButton,
  ReferenceField,
  TextField,
  useListContext,
  useRecordContext,
  BulkExportButton,
  useDataProvider
} from "react-admin";
import Box from "@mui/material/Box";
import MergeColumnMoneyField from "../../../CustomInputs/CustomField/MergeColumnMoneyField";
import ColoredNumberField from "../../../CustomInputs/ColoredNumberField";
import RestoreButton from "../../Section/Buttons/RestoreButton";
import ConfirmDeleteButton from "../../../ModalWindow/ConfirmButton";
import {loanListStylesMobile} from "./styles";

const SxLoanList = {
  '& .column-status': {
    display: {sm: 'none', md: 'table-cell'},
  },
  '& .column-return_total': {
    display: {sm: 'none', md: 'table-cell'},
  },
  '& .column-next_payment_date': {
    display: {sm: 'none', md: 'table-cell'},
  }
}

const SxLoanTrash = {
  '& .column-return_total': {
    display: {sm: 'none', md: 'table-cell'},
  },
  '& .column-status': {
    display: {sm: 'none', md: 'table-cell'},
  },
}

export const CustomerFiled = ({name}) => {
  const record = useRecordContext();
  return record ? <span>{record[name]}</span> : null;
};

const LoansSummary = () => {
  const { filterValues } = useListContext();
  const [summary, setSummary] = useState(null);

  const summaryFilters = useMemo(() => ({
    customer_id: filterValues.customer_id,
    status: filterValues.status,
    start_created: filterValues.start_created,
    end_created: filterValues.end_created,
    paranoid: filterValues.paranoid
  }), [
    filterValues.customer_id,
    filterValues.status,
    filterValues.start_created,
    filterValues.end_created,
    filterValues.paranoid
  ]);

  useEffect(() => {
    fetch('/api/loans/summary', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ 
        paranoid: summaryFilters.paranoid !== false,
        filter: summaryFilters 
      })
    })
    .then(res => res.json())
    .then(data => setSummary(data))
    .catch(console.error);
  }, [summaryFilters]);

  if (!summary) {
    return null;
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexWrap: 'wrap',
      gap: 3, 
      p: 2, 
      mt: 1,
      color: 'text.secondary',
      fontSize: '13px',
      borderTop: '1px solid #eee'
    }}>
      <span><strong>{summary.total_loans}</strong> loans</span>
      <span>Amount: <strong>${summary.total_amount.toLocaleString()}</strong></span>
      <span>Actual Returns: <strong>${summary.total_returns.toLocaleString()}</strong></span>
      <span>Expected Returns: <strong>${summary.total_expected_returns.toLocaleString()}</strong></span>
      <span style={{ color: summary.total_profit >= 0 ? 'green' : 'red' }}>
        Actual Profit: <strong>${summary.total_profit.toLocaleString()}</strong>
      </span>
      <span style={{ color: summary.total_expected_profit >= 0 ? 'green' : 'red' }}>
        Expected Profit: <strong>${summary.total_expected_profit.toLocaleString()}</strong>
      </span>
    </Box>
  );
};

const LoanListDesktop = () => {
  const {filterValues: {paranoid = true}, data} = useListContext();
  
  if (!data?.length) {
    return null;
  }
  
  return (
    <>
      {!paranoid ? (
        <Datagrid
          sx={SxLoanTrash}
          bulkActionButtons={false}
          rowStyle={loanListStylesMobile(true)}
        >
          <TextField source='id'/>
          <ColoredNumberField source="loan_amount"/>
          <MergeColumnMoneyField
            label='Return total | Profit'
            source='return_total'
            one='return_total'
            two='profit'
          />
          <TextField source='status'/>
          <DateField source='created_at'/>
          <DateField source='deleted_at'/>
          <RestoreButton resource='loans'/>
          <ConfirmDeleteButton
            resource='loans/destroy'
            confirmContent='Are you sure you want to delete? Data cannot be recovered'
          />
        </Datagrid>
      ) : (
        <Datagrid
          sx={SxLoanList}
          rowStyle={loanListStylesMobile(false)}
          bulkActionButtons={<BulkExportButton/>}
        >
          <TextField source='id'/>
          <ReferenceField source="customer_id" reference="customers">
            <CustomerFiled name='email'/>
          </ReferenceField>
          <ColoredNumberField source="loan_amount"/>
          <ColoredNumberField source="payment_amount"/>
          <MergeColumnMoneyField
            label='Return total | Profit'
            source='return_total'
            one='return_total'
            two='profit'
          />
          <DateField source='created_at'/>
          <TextField source='status'/>
          <EditButton basepath='/loans'/>
        </Datagrid>
      )}
      
      <LoansSummary />
    </>
  );
};

export default LoanListDesktop;